/**
 * De actieknoppen onder een melding.
 *
 * Spec: specs/20260831-domotica-meldingen-bewonerstablet/spec.md FR-21 t/m FR-24
 * Plan: §8.1, §6.6
 * Taken: T-4.01, T-4.08
 *
 * ── VIJF TYPES, TWEE PLEKKEN WAAR ZE UITGEVOERD WORDEN ─────────────────────
 *
 *   `dismiss`          op de tablet — sluit de melding, zet `acknowledged`
 *   `navigate`         op de tablet — maar UITSLUITEND naar een route uit de allowlist
 *   `call`             op de tablet — via de bestaande SIP-laag
 *   `domotics_output`  op de SERVER — achter zeven gates
 *   `automation_event` op de SERVER — emitteert een `internal_event`
 *
 * ── DE ALLOWLIST IS DE HELE BEVEILIGING VAN `navigate` ─────────────────────
 *
 * (T-4.01.) `route` komt uit een automation-configuratie. Zonder allowlist zou een
 * flowauteur — of iemand die een flow kan bewerken — de WebView naar een willekeurige site
 * kunnen sturen, en op een kiosk zonder adresbalk komt de bewoner daar niet meer vanaf. De
 * lijst staat in `@caro/shared/types` en is gespiegeld in de API-constante, zodat de
 * portaalvalidatie een onmogelijke route als FOUT kan tonen in plaats van hem op 800
 * toestellen stil te laten mislukken.
 *
 * ⚠ Exacte vergelijking, geen prefix: `startsWith('/')` zou `//evil.test` doorlaten — dat is
 * een protocol-relatieve URL en de browser leest hem als een ander domein.
 *
 * ── GEEN GROEN VINKJE OP EEN COMMANDO DAT NIET AANKWAM ─────────────────────
 *
 * (T-4.08.) `mqttService.publish` op de server geeft `false` TERUG in plaats van te gooien;
 * de actie-endpoint controleert dat en antwoordt met 502. Die uitkomst wordt hier ZICHTBAAR:
 * de knop krijgt een foutstaat en een regel eronder, en de melding blijft staan zodat de
 * bewoner het opnieuw kan proberen. Een knop die na een mislukte publish gewoon dichtklapt,
 * is een knop die liegt.
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as NotificationStore from '../services/NotificationStore';
import { useSip } from '../hooks/useSip';
import {
  isAllowedNotificationRoute,
  type ResidentNotificationAction,
  type ResidentNotificationActionStyle,
} from '@caro/shared/types';

type ButtonState = 'idle' | 'busy' | 'error';

export interface NotificationActionsProps {
  notificationId: string;
  actions: ResidentNotificationAction[];
  /** Sluit de melding. `acknowledge` onderscheidt "weggeklikt" van "vanzelf weg". */
  onClose: (acknowledge: boolean) => void;
  /** De sluitknop van dit scherm is minstens zo hoog. Fullpage 80, dialoog 64. */
  minHeight?: number;
}

export function NotificationActions({
  notificationId,
  actions,
  onClose,
  minHeight = 64,
}: NotificationActionsProps) {
  const navigate = useNavigate();
  const { makeCall } = useSip();
  const [states, setStates] = useState<Record<string, ButtonState>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const run = useCallback(
    async (action: ResidentNotificationAction) => {
      // Dubbelklikken op een wandtablet is eerder regel dan uitzondering. Een tweede
      // `domotics_output` terwijl de eerste loopt, is een tweede commando naar hetzelfde
      // relais.
      if (states[action.id] === 'busy') return;

      if (action.type === 'dismiss') {
        onClose(true);
        return;
      }

      if (action.type === 'navigate') {
        if (!isAllowedNotificationRoute(action.route)) {
          // Weigeren én tonen. Stil niets doen laat de bewoner denken dat de tablet stuk is,
          // en laat de beheerder nooit weten dat zijn flow een onmogelijke route bevat.
          setStates((s) => ({ ...s, [action.id]: 'error' }));
          setErrors((e) => ({ ...e, [action.id]: 'Deze bestemming is niet toegestaan.' }));
          return;
        }
        onClose(true);
        navigate(action.route);
        return;
      }

      if (action.type === 'call') {
        onClose(true);
        try {
          await makeCall(action.extension);
        } catch {
          /* de belweg heeft zijn eigen schermen en foutafhandeling */
        }
        return;
      }

      // `domotics_output` en `automation_event`: de server beslist, en de server meet.
      setStates((s) => ({ ...s, [action.id]: 'busy' }));
      const result = await NotificationStore.invokeServerAction(notificationId, action.id);
      if (result.ok) {
        onClose(true);
        return;
      }
      setStates((s) => ({ ...s, [action.id]: 'error' }));
      setErrors((e) => ({ ...e, [action.id]: messageFor(result.code) }));
    },
    [states, onClose, navigate, makeCall, notificationId],
  );

  if (!Array.isArray(actions) || actions.length === 0) return null;

  // Max 3 (plan §8.1). Afkappen in plaats van weigeren: een vierde knop is geen reden om
  // een brandmelding zonder knoppen te tonen.
  const shown = actions.slice(0, 3);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {shown.map((action) => {
          const state = states[action.id] || 'idle';
          return (
            <button
              key={action.id}
              type="button"
              onClick={() => { void run(action); }}
              disabled={state === 'busy'}
              data-testid={`notification-action-${action.id}`}
              data-state={state}
              style={{
                flex: '1 1 40%',
                minWidth: 160,
                minHeight,
                padding: '14px 18px',
                borderRadius: 14,
                border: state === 'error' ? '3px solid var(--caro-error)' : 'none',
                backgroundColor: state === 'error'
                  ? 'var(--caro-card-bg)'
                  : accentFor(action.style),
                color: state === 'error' ? 'var(--caro-error)' : 'var(--caro-card-bg)',
                fontSize: 21,
                fontWeight: 700,
                cursor: state === 'busy' ? 'default' : 'pointer',
                opacity: state === 'busy' ? 0.6 : 1,
              }}
            >
              {/* De staat staat in WOORDEN, niet alleen in kleur: kleur mag nooit de enige
                  drager zijn, en op een vergeeld kioskscherm is rood soms bruin. */}
              {state === 'busy' ? 'Bezig…' : action.label}
            </button>
          );
        })}
      </div>

      {shown.map((action) => (
        states[action.id] === 'error' && errors[action.id] ? (
          <p
            key={`err-${action.id}`}
            role="alert"
            data-testid={`notification-action-error-${action.id}`}
            style={{ margin: 0, fontSize: 17, color: 'var(--caro-error)', fontWeight: 600 }}
          >
            {errors[action.id]}
          </p>
        ) : null
      ))}
    </div>
  );
}

function accentFor(style?: ResidentNotificationActionStyle): string {
  if (style === 'danger') return 'var(--caro-error)';
  if (style === 'secondary') return 'var(--caro-success)';
  return 'var(--caro-primary)';
}

/**
 * Wat de bewoner leest wanneer het misgaat.
 *
 * ⚠ Geen technische codes op een tablet in een woonkamer. Maar ook geen "er ging iets mis":
 * het verschil tussen "de opdracht is niet aangekomen" (probeer opnieuw) en "deze knop kan
 * niet meer" (de melding is verlopen) bepaalt wat iemand vervolgens doet.
 */
function messageFor(code?: string): string {
  switch (code) {
    case 'command_not_delivered':
      return 'De opdracht is niet aangekomen. Probeer het opnieuw.';
    case 'schema_not_ready':
      return 'Deze knop is tijdelijk niet beschikbaar.';
    case 'offline':
      return 'Geen verbinding. Probeer het opnieuw.';
    case 'not_found':
      // De server antwoordt 404 op élke geweigerde gate — ook op "verlopen" en op "deze
      // output mag niet". Dat onderscheid komt bewust NIET naar de tablet: een specifieker
      // antwoord zou verklappen welk id in een ander appartement bestaat.
      return 'Deze melding is niet meer geldig.';
    default:
      return 'Het is niet gelukt. Probeer het opnieuw.';
  }
}

export default NotificationActions;
